import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AudioState, IAudioState } from '@models/audio-state';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class AudioService {
  private audioEventList: string[] = [
    'ended',
    'error',
    'play',
    'playing',
    'pause',
    'timeupdate',
    'canplaythrough',
    'loadedmetadata',
    'loadstart',
  ];
  private audioStateList$: BehaviorSubject<IAudioState>[] = [];
  private currentTimeStateList$: BehaviorSubject<number>[] = [];
  private stop$ = new Subject();

  constructor(private notificationService: NotificationService) {}

  public loadStream(
    audio$: BehaviorSubject<IAudioState>,
    audioObj: HTMLAudioElement,
    audioState: IAudioState,
    currentTime$: BehaviorSubject<number>,
    url: string,
  ): Observable<Event> {
    this.updateAndResetStreamList(audio$, audioState, currentTime$);
    return this.streamObservable(audio$, audioObj, audioState, url).pipe(
      takeUntil(this.stop$),
    );
  }

  public pause(audioObj: HTMLAudioElement): void {
    audioObj.pause();
  }

  public restart(audioObj: HTMLAudioElement): void {
    audioObj.pause();
    audioObj.currentTime = 0;
    audioObj.play();
  }

  public resume(audioObj: HTMLAudioElement): void {
    audioObj.play();
  }

  public stop(): void {
    this.stop$.next();
  }

  public streamObservable(
    audio$: BehaviorSubject<IAudioState>,
    audioObj: HTMLAudioElement,
    audioState: IAudioState,
    url: string,
  ): Observable<Event> {
    return new Observable<Event>((observer) => {
      if (!url) {
        this.notificationService.showError([
          'There is no audio file for this text.',
        ]);
      }
      audioObj.src = url;
      audioObj.load();
      audioObj.play().catch((err) => {
        console.log('There is an issue with this audio file');
        this.resetAudioState(audioState);
        audioState.hasError = true;
      });

      const handler = (event: Event) => {
        this.updateAudioState(audio$, audioObj, audioState, event);
        observer.next(event);
      };

      this.addEvents(audioObj, this.audioEventList, handler);
      return () => {
        audioObj.pause();
        audioObj.currentTime = 0;
        this.removeEvents(audioObj, this.audioEventList, handler);
        this.resetAudioState(audioState);
      };
    });
  }

  private updateAndResetStreamList(
    audio$: BehaviorSubject<IAudioState>,
    audioState: IAudioState,
    currentTime$: BehaviorSubject<number>,
  ) {
    this.audioStateList$.push(audio$);
    this.audioStateList$.forEach((audioStream) => {
      audioStream.next(new AudioState());
      this.stop();
    });
    audioState.hasStarted = true;
    audio$.next(audioState);

    this.currentTimeStateList$.push(currentTime$);
    this.currentTimeStateList$.forEach((time) => {
      time.next(0);
    });
  }

  private addEvents(
    audio: HTMLAudioElement,
    eventList: string[],
    handler: (event: Event) => void,
  ): void {
    eventList.forEach((event) => audio.addEventListener(event, handler));
  }

  private removeEvents(
    audio: HTMLAudioElement,
    eventList: string[],
    handler: (event: Event) => void,
  ): void {
    eventList.forEach((event) => audio.removeEventListener(event, handler));
  }

  private resetAudioState(audioState: IAudioState): void {
    audioState = new AudioState();
  }

  private updateAudioState(
    audio$: BehaviorSubject<IAudioState>,
    audioObj: HTMLAudioElement,
    audioState: IAudioState,
    event: Event,
  ): void {
    switch (event.type) {
      case 'canplaythrough':
        audioState.duration = audioObj.duration;
        audioState.canPlay = true;
        break;
      case 'ended':
        audioState.hasStarted = false;
        break;
      case 'playing':
        audioState.isPlaying = true;
        break;
      case 'pause':
        audioState.isPlaying = false;
        break;
      case 'timeupdate':
        audioState.currentTime = audioObj.currentTime;
        break;
      case 'error':
        this.notificationService.showError(['Unable to play audio file.']);
        this.resetAudioState(audioState);
        audioState.hasError = true;
        break;
    }
    audio$.next(audioState);
  }
}
